import type { CVT, CvtErrorSbrResponseData, CvtStatusData, GetCVTsResponse } from '@packages/models/api/cvt';

import { useAbortController } from '@packages/core/http';
import { getLogger } from '@packages/core/logging';
import { CVT_STATUS_MAP, CvtStatus } from '@packages/models/api/cvt';
import { uniqBy } from 'lodash';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';

import type { CvtService } from '../cvt';
import type { QmrsService } from '../qmrs';
import type { UploadedCvtDiagnostic } from './qmr-cvt-diagnostic.state';

import { deleteQmrCvtChallengeDetails, fetchQmr, patchQmr, qmrActions, useQmrDispatch, useQmrState } from '../qmrs';
import { CvtDiagnosticUploadsDispatchContext, CvtDiagnosticUploadsStateContext } from './qmr-cvt-diagnostic.context';
import { cvtDiagnosticUploadsActions } from './qmr-cvt-diagnostic.state';
import { isNoResultErrorType, isUploadedCvtDiagnosticFinished, isValidCvtDiagnostic } from './qmr-cvt-diagnostic.utils';

const logger = getLogger('qmr');

const POLL_INTERVAL_MS = 5000;
const POLL_TIME_LIMIT_MS = 30 * 60 * 1000;

const DIAGNOSTICS_PAGE_SIZE = 10;

type UseQmrCvtDiagnosticParams = {
    /** Checks the uploaded diagnostic's status. */
    cvtService: CvtService;

    /**
     * Whether the report's diagnostic can be changed. The uploaded diagnostic is polled only while it can, and not
     * while the report is being submitted or left.
     */
    isEnabled: boolean;

    /** Callback fired once the report's diagnostic is updated, by the poll's attach or a change. */
    onDiagnosticUpdate: () => void;

    /** Deletes the CVT challenge, links the diagnostic and reads the report again. */
    qmrsService: QmrsService;
};

export function useCvtDiagnosticUploadsDispatch() {
    const dispatch = useContext(CvtDiagnosticUploadsDispatchContext);

    if (dispatch === undefined) {
        throw new Error('useCvtDiagnosticUploadsDispatch must be used within a CvtDiagnosticUploadsProvider');
    }

    return dispatch;
}

export function useCvtDiagnosticUploadsState() {
    const state = useContext(CvtDiagnosticUploadsStateContext);

    if (state === undefined) {
        throw new Error('useCvtDiagnosticUploadsState must be used within a CvtDiagnosticUploadsProvider');
    }

    return state;
}

/**
 * Polls the diagnostic uploaded from the report until it's attached or can't be, and updates which diagnostic the
 * report holds.
 */
export function useQmrCvtDiagnostic({
    cvtService,
    isEnabled,
    onDiagnosticUpdate,
    qmrsService,
}: UseQmrCvtDiagnosticParams) {
    const { abortSignalRef: checkAbortSignal, signalAbort: signalCheckAbort } = useAbortController();
    const { abortSignalRef: updateAbortSignal, signalAbort: signalUpdateAbort } = useAbortController();
    const { cvtChallengeDetailsSaveCount, isLeaving, isSubmitting, isUpdatingCvtDiagnostic, localPatch, qmr } =
        useQmrState();
    const qmrDispatch = useQmrDispatch();
    const cvtDiagnosticUploads = useCvtDiagnosticUploadsState();
    const cvtDiagnosticUploadsDispatch = useCvtDiagnosticUploadsDispatch();

    const [isCheckingStatus, setIsCheckingStatus] = useState<boolean>(false);

    const [lastAttachedDiagnosticId, setLastAttachedDiagnosticId] = useState<string>();

    const latestPollRef = useRef<() => Promise<void>>(pollUploadedDiagnostic);

    const uploadedDiagnostic = qmr ? cvtDiagnosticUploads[qmr.qmrId] : undefined;
    const isPolling =
        isEnabled &&
        !isLeaving &&
        !isSubmitting &&
        Boolean(uploadedDiagnostic) &&
        !isUploadedCvtDiagnosticFinished(uploadedDiagnostic);

    const isReportBeingWritten = isLeaving || isSubmitting || isUpdatingCvtDiagnostic;
    const isSavingCvtChallengeDetails = cvtChallengeDetailsSaveCount > 0;

    /**
     * Polls the uploaded diagnostic. Each tick checks the test's status, then attaches it or holds why it can't be
     * attached. A No Result test keeps its record after the attach, so its alert can show. The poll drops a diagnostic
     * it's still polling once the report holds its test, and past the time limit gives up after one more check, or at
     * once if a check is still out. A tick is skipped while the report's diagnostic is being updated, so the time limit
     * can't fail a test mid-attach, and while the CVT challenge is being saved, since the attach deletes it. The
     * check's signal is read before its state update, which can run a waiting cancel first, and checked again once
     * the check returns, since a cancel can land after its response is in. Catches every failure, since the interval
     * ignores the promise.
     */
    async function pollUploadedDiagnostic() {
        if (!uploadedDiagnostic) {
            return;
        }

        if (isUpdatingCvtDiagnostic || isSavingCvtChallengeDetails) {
            return;
        }

        if (uploadedDiagnostic.diagnosticId === qmr?.cvtDiagnosticsJobId) {
            cvtDiagnosticUploadsDispatch(
                cvtDiagnosticUploadsActions.clearUploadedDiagnostic({ qmrId: uploadedDiagnostic.qmrId })
            );
            return;
        }

        const isPastTimeLimit = Date.now() - uploadedDiagnostic.uploadedAt.getTime() >= POLL_TIME_LIMIT_MS;

        if (isCheckingStatus && isPastTimeLimit) {
            endPollAtTimeLimit();
            return;
        }

        if (isCheckingStatus) {
            return;
        }

        const signal = checkAbortSignal.current;

        setIsCheckingStatus(true);

        try {
            const response = await cvtService.getCvtStatus(uploadedDiagnostic.diagnosticId, true, signal);

            if (signal.aborted || (!response.success && response.aborted)) {
                return;
            }

            const isStillPolling = !response.success || CVT_STATUS_MAP[response.data.status] === 'Pending';

            if (isStillPolling && isPastTimeLimit) {
                endPollAtTimeLimit();
                return;
            }

            if (isStillPolling) {
                return;
            }

            const errorSbrResponseData = toErrorSbrResponseData(response.data);
            const uploadedDiagnosticWithResult: UploadedCvtDiagnostic = {
                ...uploadedDiagnostic,
                errorDetail: errorSbrResponseData?.errorDetail,
                errorType: errorSbrResponseData?.errorType,
                status: response.data.status,
                stsErrorResponse: response.data.stsErrorResponse,
            };

            if (!isValidCvtDiagnostic(response.data)) {
                cvtDiagnosticUploadsDispatch(
                    cvtDiagnosticUploadsActions.setUploadedDiagnostic({
                        uploadedDiagnostic: uploadedDiagnosticWithResult,
                    })
                );
                return;
            }

            await attachUploadedDiagnostic(uploadedDiagnosticWithResult);
        } catch {
            cvtDiagnosticUploadsDispatch(
                cvtDiagnosticUploadsActions.setUploadedDiagnostic({
                    uploadedDiagnostic: { ...uploadedDiagnostic, hasAttachError: true },
                })
            );
        } finally {
            setIsCheckingStatus(false);
        }
    }

    /**
     * Ends the poll with the attach error when the uploaded diagnostic is still processing past the time limit, and
     * cancels a check still out.
     */
    function endPollAtTimeLimit() {
        if (!uploadedDiagnostic) {
            return;
        }

        logger.warn(`CVT diagnostic [${uploadedDiagnostic.diagnosticId}] was still processing at the time limit`, {
            diagnosticId: uploadedDiagnostic.diagnosticId,
        });
        signalCheckAbort();
        cvtDiagnosticUploadsDispatch(
            cvtDiagnosticUploadsActions.setUploadedDiagnostic({
                uploadedDiagnostic: { ...uploadedDiagnostic, hasAttachError: true },
            })
        );
    }

    /**
     * Attaches the uploaded diagnostic. A failed attach keeps the test's result with the attach error, so the alert
     * can still link to it.
     */
    async function attachUploadedDiagnostic(uploadedDiagnosticWithResult: UploadedCvtDiagnostic) {
        try {
            if (isNoResultErrorType(uploadedDiagnosticWithResult)) {
                await updateDiagnostic(uploadedDiagnosticWithResult.diagnosticId, uploadedDiagnosticWithResult);
                return;
            }

            await updateDiagnostic(uploadedDiagnosticWithResult.diagnosticId);
        } catch {
            cvtDiagnosticUploadsDispatch(
                cvtDiagnosticUploadsActions.setUploadedDiagnostic({
                    uploadedDiagnostic: { ...uploadedDiagnosticWithResult, hasAttachError: true },
                })
            );
        }
    }

    /** Resolves `false` when there is no report or the request is cancelled. */
    const deleteChallenge = useCallback(
        async (signal: AbortSignal): Promise<boolean> => {
            if (!qmr?.qmrId) {
                return false;
            }

            const updatedQmr = await deleteQmrCvtChallengeDetails({
                qmrId: qmr.qmrId,
                qmrsService,
                qmrDispatch,
                signal,
            });

            return Boolean(updatedQmr);
        },
        [qmr?.qmrId, qmrDispatch, qmrsService]
    );

    /**
     * A CVT challenge, whether saved or still held in `localPatch`, is deleted first, because it was made against the
     * old diagnostic. Every request takes the signal read at the start, so a cancel stops the ones after it too. It is
     * read before the state update, which can run a waiting cancel first.
     */
    const updateDiagnostic = useCallback(
        async (diagnosticId: string, uploadedDiagnosticWithResult?: UploadedCvtDiagnostic): Promise<boolean> => {
            if (!qmr?.qmrId) {
                return false;
            }

            const signal = updateAbortSignal.current;

            qmrDispatch(qmrActions.setIsUpdatingCvtDiagnostic(true));

            try {
                const isChallengeDeletionHeld = localPatch?.cvtChallengeDetails === null;

                if (qmr.cvtChallengeDetails || isChallengeDeletionHeld) {
                    const isChallengeDeleted = await deleteChallenge(signal);

                    if (!isChallengeDeleted) {
                        return false;
                    }
                }

                if (localPatch?.cvtChallengeDetails) {
                    patchQmr({
                        qmrId: qmr.qmrId,
                        qmrsService,
                        qmrDispatch,
                        isLocalPatch: true,
                        qmrPatch: { cvtChallengeDetails: null },
                    });
                }

                const response = await qmrsService.updateCvtDiagnostic({
                    qmrId: qmr.qmrId,
                    cvtDiagnosticsJobId: diagnosticId,
                    signal,
                });

                if (!response.success && response.aborted) {
                    return false;
                } else if (!response.success) {
                    throw response.data;
                }

                const updatedQmr = await fetchQmr({
                    qmrId: qmr.qmrId,
                    qmrsService,
                    qmrDispatch,
                    ignoreCache: true,
                    signal,
                });

                if (!updatedQmr) {
                    return false;
                }

                if (!updatedQmr.cvtDiagnosticsJobId) {
                    throw new Error('The report came back without the test.');
                }

                if (uploadedDiagnosticWithResult) {
                    cvtDiagnosticUploadsDispatch(
                        cvtDiagnosticUploadsActions.setUploadedDiagnostic({
                            uploadedDiagnostic: uploadedDiagnosticWithResult,
                        })
                    );
                } else {
                    cvtDiagnosticUploadsDispatch(
                        cvtDiagnosticUploadsActions.clearUploadedDiagnostic({ qmrId: qmr.qmrId })
                    );
                }

                setLastAttachedDiagnosticId(diagnosticId);
                onDiagnosticUpdate();

                return true;
            } catch (error) {
                logger.error(`Failed to update the CVT diagnostic on the report [${qmr.qmrId}]`, {
                    diagnosticId,
                    error,
                    qmrId: qmr.qmrId,
                });

                throw error;
            } finally {
                qmrDispatch(qmrActions.setIsUpdatingCvtDiagnostic(false));
            }
        },
        [
            cvtDiagnosticUploadsDispatch,
            deleteChallenge,
            localPatch,
            onDiagnosticUpdate,
            qmr,
            qmrDispatch,
            qmrsService,
            updateAbortSignal,
        ]
    );

    /**
     * Cancels a status check and an update still running when the report, `isEnabled`, `isSubmitting` or `isLeaving`
     * changes, so neither lands on another report, once the report's diagnostic can't be changed, or while the report
     * is being submitted or left.
     */
    useEffect(() => {
        signalCheckAbort();
        signalUpdateAbort();
    }, [isEnabled, isLeaving, isSubmitting, qmr?.qmrId, signalCheckAbort, signalUpdateAbort]);

    /**
     * Cancels a status check still out when a CVT challenge save starts, so the poll can't attach the test mid-save,
     * since the attach deletes the challenge. The upload stays pending, and a tick after the save checks it again.
     */
    useEffect(() => {
        if (!isSavingCvtChallengeDetails) {
            return;
        }

        signalCheckAbort();
    }, [isSavingCvtChallengeDetails, signalCheckAbort]);

    useEffect(() => {
        latestPollRef.current = pollUploadedDiagnostic;
    });

    /**
     * Runs the poll on an interval while it's on. It calls the latest poll through the ref, so the interval doesn't
     * restart on every render.
     */
    useEffect(() => {
        if (!isPolling) {
            return;
        }

        const intervalId = setInterval(() => latestPollRef.current(), POLL_INTERVAL_MS);

        return () => clearInterval(intervalId);
    }, [isPolling]);

    /** Clears the uploaded diagnostic when the report is left, if it finished, so only one still processing is kept. */
    useEffect(() => {
        if (!qmr?.qmrId) {
            return;
        }

        return () => {
            cvtDiagnosticUploadsDispatch(
                cvtDiagnosticUploadsActions.clearFinishedUploadedDiagnostic({ qmrId: qmr.qmrId })
            );
        };
    }, [cvtDiagnosticUploadsDispatch, qmr?.qmrId]);

    /**
     * Reads the report again whenever it stops holding the test the last update attached: a save whose response the
     * server built before the attach can arrive after the update's own read and put the old test back, and several
     * such saves can be out. Waits while the report is being written, so the read can't race those writes.
     */
    useEffect(() => {
        if (!qmr?.qmrId || !lastAttachedDiagnosticId) {
            return;
        }

        if (isReportBeingWritten) {
            return;
        }

        if (qmr.cvtDiagnosticsJobId === lastAttachedDiagnosticId) {
            return;
        }

        fetchQmr({
            qmrId: qmr.qmrId,
            qmrsService,
            qmrDispatch,
            ignoreCache: true,
            signal: updateAbortSignal.current,
        }).catch((error) => {
            logger.error(`Failed to read the report again after its CVT diagnostic was updated [${qmr.qmrId}]`, {
                error,
                qmrId: qmr.qmrId,
            });
        });
    }, [
        isReportBeingWritten,
        lastAttachedDiagnosticId,
        qmr?.cvtDiagnosticsJobId,
        qmr?.qmrId,
        qmrDispatch,
        qmrsService,
        updateAbortSignal,
    ]);

    return {
        /**
         * Whether the report's diagnostic is being updated, by the poll's attach or a change. Held in `QmrState` as
         * `isUpdatingCvtDiagnostic`, so the header can hold Submit while it runs.
         */
        isUpdatingDiagnostic: isUpdatingCvtDiagnostic,

        /**
         * Updates the report's CVT diagnostic, then clears the uploaded one. When it's given
         * `uploadedDiagnosticWithResult`, as the poll does for a No Result run, it keeps that instead, so the run's
         * alert can show. Resolves `false` when there is no report or a request was cancelled.
         */
        updateDiagnostic,

        /**
         * The diagnostic uploaded from the report, until it's attached or cleared. A No Result test stays after it's
         * attached, so its alert can show.
         */
        uploadedDiagnostic,
    };
}

/** Fetches the report's CVT diagnostics that can be attached, a page at a time, for the change modal's list. */
export function useQmrValidCvtDiagnostics({ qmrsService }: { qmrsService: QmrsService }) {
    const { abortSignalRef, signalAbort } = useAbortController();
    const { qmr } = useQmrState();

    const [diagnosticOptions, setDiagnosticOptions] = useState<CVT[]>([]);
    const [hasFailedToLoadDiagnostics, setHasFailedToLoadDiagnostics] = useState<boolean>(false);
    const [isLoadingDiagnostics, setIsLoadingDiagnostics] = useState<boolean>(false);

    const [hasFailedToLoadMoreDiagnostics, setHasFailedToLoadMoreDiagnostics] = useState<boolean>(false);
    const [isLoadingMoreDiagnostics, setIsLoadingMoreDiagnostics] = useState<boolean>(false);
    const [nextPage, setNextPage] = useState<number>();

    const fetchDiagnostics = useCallback(async () => {
        if (!qmr?.qmrId) {
            return;
        }

        signalAbort();

        const signal = abortSignalRef.current;

        setHasFailedToLoadDiagnostics(false);
        setHasFailedToLoadMoreDiagnostics(false);
        setIsLoadingDiagnostics(true);
        setIsLoadingMoreDiagnostics(false);

        try {
            const validCvtDiagnostics = await fetchValidCvtDiagnostics({
                qmrId: qmr.qmrId,
                page: 0,
                qmrsService,
                signal,
            });

            if (!validCvtDiagnostics || signal.aborted) {
                return;
            }

            setDiagnosticOptions(validCvtDiagnostics.diagnosticOptions);
            setIsLoadingDiagnostics(false);
            setNextPage(validCvtDiagnostics.nextPage);
        } catch (error) {
            if (signal.aborted) {
                return;
            }

            setHasFailedToLoadDiagnostics(true);
            setIsLoadingDiagnostics(false);
            logger.error(`Failed to fetch the CVT diagnostics for the report [${qmr.qmrId}]`, {
                error,
                qmrId: qmr.qmrId,
            });
        }
    }, [abortSignalRef, qmr?.qmrId, qmrsService, signalAbort]);

    const loadDiagnostics = useCallback(() => {
        setDiagnosticOptions([]);
        setNextPage(undefined);
        fetchDiagnostics();
    }, [fetchDiagnostics]);

    /**
     * Adds the next page to the list, leaving out any diagnostic it already holds. A run that finishes between two
     * fetches pushes the ones after it down a row, so the next page can repeat one. The signal is read before the
     * state update, which can run a waiting cancel first, and checked again once the fetch returns, since a cancel
     * can land after its response is in.
     */
    const fetchMoreDiagnostics = useCallback(async () => {
        if (!qmr?.qmrId || nextPage === undefined || isLoadingDiagnostics || isLoadingMoreDiagnostics) {
            return;
        }

        const signal = abortSignalRef.current;

        setHasFailedToLoadMoreDiagnostics(false);
        setIsLoadingMoreDiagnostics(true);

        try {
            const validCvtDiagnostics = await fetchValidCvtDiagnostics({
                qmrId: qmr.qmrId,
                page: nextPage,
                qmrsService,
                signal,
            });

            if (!validCvtDiagnostics || signal.aborted) {
                return;
            }

            setDiagnosticOptions((previous) =>
                uniqBy([...previous, ...validCvtDiagnostics.diagnosticOptions], (diagnostic) => diagnostic.diagnosticId)
            );
            setIsLoadingMoreDiagnostics(false);
            setNextPage(validCvtDiagnostics.nextPage);
        } catch (error) {
            if (signal.aborted) {
                return;
            }

            setHasFailedToLoadMoreDiagnostics(true);
            setIsLoadingMoreDiagnostics(false);
            logger.error(`Failed to fetch more CVT diagnostics for the report [${qmr.qmrId}]`, {
                error,
                page: nextPage,
                qmrId: qmr.qmrId,
            });
        }
    }, [abortSignalRef, isLoadingDiagnostics, isLoadingMoreDiagnostics, nextPage, qmr?.qmrId, qmrsService]);

    /**
     * Starts the list over when the report changes. It cancels a fetch still running and empties the list, so one
     * report's tests can't show, or load more, on another.
     */
    useEffect(() => {
        signalAbort();
        setDiagnosticOptions([]);
        setHasFailedToLoadDiagnostics(false);
        setHasFailedToLoadMoreDiagnostics(false);
        setIsLoadingDiagnostics(false);
        setIsLoadingMoreDiagnostics(false);
        setNextPage(undefined);
    }, [qmr?.qmrId, signalAbort]);

    return {
        /** The list's diagnostics, the ones that completed with symptoms or with No Result. */
        diagnosticOptions,

        /** Fetches the list again from its first page, keeping the shown list until the new one arrives. */
        fetchDiagnostics,

        /** Fetches the next page and adds it to the list. */
        fetchMoreDiagnostics,

        /** Whether the list's first page failed to load. */
        hasFailedToLoadDiagnostics,

        /** Whether the list's next page failed to load. */
        hasFailedToLoadMoreDiagnostics,

        /** Whether the backend has pages the list hasn't fetched yet. */
        hasMoreDiagnostics: nextPage !== undefined,

        /** Whether the list's first page is being fetched. */
        isLoadingDiagnostics,

        /** Whether the list's next page is being fetched. */
        isLoadingMoreDiagnostics,

        /** Empties the list, then fetches it, so the change modal opens on its loading look. */
        loadDiagnostics,
    };
}

/** Fetches a page of the report's completed runs. Resolves `undefined` when cancelled. */
async function fetchCompletedRuns({
    qmrId,
    page,
    qmrsService,
    signal,
}: {
    qmrId: string;
    page: number;
    qmrsService: QmrsService;
    signal: AbortSignal;
}): Promise<GetCVTsResponse | undefined> {
    const response = await qmrsService.fetchValidCvtDiagnostics({
        qmrId,
        ignoreCache: true,
        signal,
        size: DIAGNOSTICS_PAGE_SIZE,
        statuses: [CvtStatus.RECEIVED_RESPONSE_FROM_SBR],
        page,
    });

    if (!response.success && response.aborted) {
        return undefined;
    } else if (!response.success) {
        throw response.data;
    }

    return response.data;
}

/**
 * Fetches the report's valid CVT diagnostics, starting from the given page (0 is the first), and returns the page to
 * fetch after them. It keeps the runs with symptoms or with No Result, since only those can be attached. When a page
 * has none, it moves on to the next. Resolves `undefined` when cancelled.
 */
async function fetchValidCvtDiagnostics({
    qmrId,
    page,
    qmrsService,
    signal,
}: {
    qmrId: string;
    page: number;
    qmrsService: QmrsService;
    signal: AbortSignal;
}): Promise<{ diagnosticOptions: CVT[]; nextPage?: number } | undefined> {
    const completedRuns = await fetchCompletedRuns({ qmrId, page, qmrsService, signal });

    if (!completedRuns) {
        return undefined;
    }

    const diagnosticOptions = (completedRuns.results ?? []).filter(isValidCvtDiagnostic);
    const nextPage = page + 1;
    const hasMorePages = nextPage * DIAGNOSTICS_PAGE_SIZE < completedRuns.totalCount;

    if (diagnosticOptions.length === 0 && hasMorePages) {
        return fetchValidCvtDiagnostics({ qmrId, page: nextPage, qmrsService, signal });
    }

    return { diagnosticOptions, nextPage: hasMorePages ? nextPage : undefined };
}

function toErrorSbrResponseData({ sbrResponseData }: CvtStatusData): CvtErrorSbrResponseData | undefined {
    if (!sbrResponseData || !('errorType' in sbrResponseData)) {
        return undefined;
    }

    return sbrResponseData;
}
